import React, { useState, useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import { InputBase, Box } from "@mui/material";

// Custom styled DataGrid
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#005EB8 !important", // Cognizant blue background
    color: "#FFFFFF !important", // White text
    borderBottom: `2px solid #003D7A`, // Darker blue border for separation
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#005EB8 !important", // Cognizant blue background
    color: "#FFFFFF !important", // White text
    padding: theme.spacing(0, 2), // Consistent padding
    fontWeight: 600, // Slightly bold headers
    "&:hover": {
      backgroundColor: "#003D7A !important", // Darker blue on hover
    },
    "&:focus": {
      backgroundColor: "#005EB8 !important",
    },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "0.95rem", // Slightly larger text
    letterSpacing: "0.5px", // Better readability
    textTransform: "uppercase", // Modern look
    color: "#FFFFFF !important", // White text
    fontWeight: 600,
  },
  "& .MuiDataGrid-columnSeparator": {
    color: "#FFFFFF !important", // White separator color
  },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid #D3D3D3`, // Light grey border for rows, matching the app
  },
  "& .MuiDataGrid-sortIcon": {
    color: "#FFFFFF !important", // White sort icon
  },
  "& .MuiDataGrid-menuIconButton": {
    color: "#FFFFFF !important", // White menu icon
  },
  "& .MuiDataGrid-iconButtonContainer": {
    color: "#FFFFFF !important",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#FFFFFF !important",
  },
}));

function DataTable({
  rows,
  columns,
  getRowId,
  searchPlaceholder = "Global Search...",
  disableSearch = false,
  height,
  hideFooter = false,
  getRowClassName,
  sx: customSx,
}) {
  const [searchText, setSearchText] = useState("");
  const gridRef = useRef(null);

  const filteredRows = useMemo(() => {
    if (disableSearch || !searchText) return rows;
    return rows.filter((row) =>
      columns.some((column) =>
        String(row[column.field] ?? "")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      )
    );
  }, [rows, columns, searchText, disableSearch]);

  return (
    <div style={{ height: height || 600, width: "100%" }}>
      {!disableSearch && (
        <Box
          sx={{
            padding: 1,
            backgroundColor: "#FFFFFF",
            borderBottom: `1px solid #D3D3D3`,
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <InputBase
            type="text"
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
            sx={{
              padding: "6px 12px",
              marginLeft: 1,
              borderRadius: 1,
              backgroundColor: "#F5F5F5",
              border: `1px solid #D3D3D3`,
              "&:focus-within": {
                borderColor: "#005EB8",
              },
              minWidth: 260,
              width: "100%",
            }}
          />
        </Box>
      )}
      <StyledDataGrid
        ref={gridRef}
        rows={filteredRows}
        getRowId={getRowId}
        columns={columns}
        pageSize={7}
        rowsPerPageOptions={hideFooter ? [] : [10, 25, 50]}
        disableVirtualization
        autoHeight={false}
        getRowClassName={getRowClassName}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#005EB8 !important",
            color: "#FFFFFF !important",
            fontWeight: 600,
            borderBottom: "2px solid #003D7A",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#005EB8 !important",
            color: "#FFFFFF !important",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#FFFFFF !important",
            fontWeight: 600,
          },
          "& .row-today": {
            backgroundColor: "#C8E6C9 !important",
            "&:hover": {
              backgroundColor: "#A5D6A7 !important",
            },
          },
          "& .row-old": {
            backgroundColor: "#FFE0B2 !important",
            "&:hover": {
              backgroundColor: "#FFCC80 !important",
            },
          },
          ...customSx,
        }}
        hideFooterSelectedRowCount
        hideFooter={hideFooter}
      />
    </div>
  );
}

export default DataTable;
